<script>
    import { createEventDispatcher, getContext, onMount } from "svelte";

    import { Link } from "svelte-routing";

    import Player from "../components/Player.svelte";
    import Loader from "../components/Loader.svelte";
    import SongItem from "../components/SongItem.svelte";
    import CollectionItem from "../components/CollectionItem.svelte";

    import { currentLocation } from "../stores.js";
    import { fetchMediaItems } from "../utils.js";

    import { TOASTS_KEY } from "../components/toasts/toast.js";

    export let closable = false;

    const dispatch = createEventDispatcher();

    const { addPromiseToast } = getContext(TOASTS_KEY);

    const browseRoutes = [{ tag: "for-you", name: "For You" }];
    const libraryRoutes = [
        { tag: "recently-added", name: "Recently Added" },
        { tag: "albums", name: "Albums" },
        { tag: "playlists", name: "Playlists" },
        { tag: "artists", name: "Artists" },
        { tag: "songs", name: "Songs" },
    ];
    const miscRoutes = [
        { tag: "export", name: "Export Library" },
        { tag: "import", name: "Import Library" },
    ];
    let currentRoute;
    currentLocation.subscribe((value) => {
        currentRoute = value;
    });

    let searchInput;
    let searchFocused = false;
    let searchText = "";
    let searchResults = Promise.reject();
    let searchTab = "songs";
    let searchOrigin = "api";

    let music = MusicKit.getInstance();

    function handleKeyDown(event) {
        if (event.key == "Enter") {
            searchResults = (async () => {
                const results = await performSearch();
                // console.log(results);
                return results;
            })();
        }
    }

    function handleGlobalKeyDown(event) {
        const { metaKey, altKey, shiftKey } = event;
        if (metaKey || altKey || shiftKey) return;
        event.preventDefault();
        if (event.key == "s") {
            searchInput.focus();
            searchFocused = true;
        }
    }

    function handleSearchResultClick(result) {
        return function () {
            const promise = (async () => {
                const items = await fetchMediaItems(result.type, result.id);
                await music.setQueue({ items });
                await music.changeToMediaAtIndex(0);
            })();
            addPromiseToast(promise, {
                pending: {
                    kind: "info",
                    content: "Scheduling the item to play now ...",
                },
                success: {
                    content: "The item is now playing !",
                    duration: 5000,
                },
                error: {
                    content: "An error happened while attempting to schedule the item.",
                    duration: 5000,
                },
            });
        };
    }

    function handleSearchResultContextMenu(item) {
        return function (event) {
            const { pageX, pageY, target } = event.detail;
            dispatch("context-menu", { item, pageX, pageY, target });
        };
    }

    async function performSearch() {
        if (searchText.length === 0) {
            throw false;
        }
        const [api, library] = await Promise.all([
            (async () => {
                const { data: { results } } = await music.api.music(`/v1/catalog/{{storefrontId}}/search`, {
                    term: searchText,
                    types: ["songs", "albums", "playlists"],
                    limit: 5,
                });
                return results;
            })(),
            (async () => {
                const { data: { results } } = await music.api.music(`/v1/me/library/search`, {
                    term: searchText,
                    types: ["library-songs", "library-albums", "library-playlists"],
                    limit: 5,
                });

                // remove `library-` prefix from all object keys.
                return Object.entries(results)
                    .map(([key, value]) => [key.replace("library-", ""), value])
                    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
            })(),
        ]);
        return { api, library };
    }

    function handleAuthButton() {
        if (music.isAuthorized) {
            music.unauthorize().finally(() => {
                music = music;
            });
        } else {
            music.authorize().finally(() => {
                music = music;
            });
        }
    }

    let itemDragged = false;
    let itemDraggedOver = false;

    function handleDragEnter(event) {
        event.preventDefault();
        itemDraggedOver = true;
    }

    function handleDragOver(event) {
        event.preventDefault();
    }

    function handleDragLeave() {
        itemDraggedOver = false;
    }

    function handleDrop(event) {
        itemDragged = false;
        itemDraggedOver = false;
        const contents = event.dataTransfer.getData("application/json");
        console.log("item dropped", contents);
        if (contents == undefined) {
            return;
        }
        const promise = (async () => {
            const item = JSON.parse(contents);
            await music.api.music(
                `/v1/me/library`,
                { [`ids[${item.type}]`]: item.id },
                { fetchOptions: { method: "POST" } },
            );
        })();
        addPromiseToast(promise, {
            pending: {
                kind: "info",
                content: "Hold tight, we are adding the item to your library ...",
            },
            success: {
                content: "The item was successfully added to your library !",
                duration: 5000,
            },
            error: {
                content: "An error happened while adding the item to your library.",
                duration: 5000,
            },
        });
    }

    function handleDragStart(event) {
        const data = event.dataTransfer.getData("application/json");
        const item = JSON.parse(data);
        const allowed = ["songs", "albums", "playlists"];
        itemDragged = allowed.includes(item.type);
    }

    function handleDragEnd() {
        itemDragged = false;
    }

    onMount(() => {
        document.addEventListener("dragstart", handleDragStart);
        document.addEventListener("dragend", handleDragEnd);

        return function () {
            document.removeEventListener("dragstart", handleDragStart);
            document.removeEventListener("dragend", handleDragEnd);
        };
    })
</script>

<div class="root">
    <div class="title">
        MusicKit Player
        {#if closable}
            <div class="icon-cross burger" on:click={() => dispatch("close")} />
        {/if}
    </div>
    <div class="options">
        <div class="option-header">Search</div>
        <div
            class="option search"
            style="cursor: initial"
            on:mouseenter={() => { searchFocused = true; }}
            on:mouseleave={() => { searchFocused = false; }}
        >
            <input
                type="text"
                placeholder="Type here to search..."
                bind:value={searchText}
                bind:this={searchInput}
                on:keydown|stopPropagation={handleKeyDown}
            />
            {#if searchFocused}
                <div class="search-results">
                    {#await searchResults}
                        <div class="search-results-message">
                            <Loader />
                        </div>
                    {:then results}
                        <div class="search-results-group-header search-results-tabs-container">
                            <div class="search-results-tabs-1">
                                <div class="search-results-tab" class:active={searchOrigin == "api"} on:click={() => { searchOrigin = "api"; }}>
                                    Apple Music
                                </div>
                                <div class="search-results-tab" class:active={searchOrigin == "library"} on:click={() => { searchOrigin = "library"; }}>
                                    Library
                                </div>
                            </div>
                        </div>
                        <div class="search-results-group-header search-results-tabs-container">
                            <div class="search-results-tabs-2">
                                <div class="search-results-tab" class:active={searchTab == "songs"} on:click={() => { searchTab = "songs"; }}>
                                    Songs
                                </div>
                                <div class="search-results-tab" class:active={searchTab == "albums"} on:click={() => { searchTab = "albums"; }}>
                                    Albums
                                </div>
                                <div class="search-results-tab" class:active={searchTab == "playlists"} on:click={() => { searchTab = "playlists"; }}>
                                    Playlists
                                </div>
                            </div>
                        </div>
                        {#if searchTab == "songs"}
                            {#if results[searchOrigin].songs}
                                {#each results[searchOrigin].songs.data as result}
                                    <div class="search-results-song-item">
                                        <SongItem
                                            class="search-results-song-item"
                                            item={result}
                                            on:click={handleSearchResultClick(result)}
                                            on:context-menu={handleSearchResultContextMenu(result)}
                                        />
                                    </div>
                                {/each}
                            {:else}
                                <div class="search-results-message">
                                    No songs results...
                                </div>
                            {/if}
                        {:else if searchTab == "albums"}
                            {#if results[searchOrigin].albums}
                                {#each results[searchOrigin].albums.data as result}
                                    <div class="search-results-song-item">
                                        <CollectionItem
                                            class="search-results-song-item"
                                            item={result}
                                            on:click={() => { dispatch("open-modal", result); }}
                                            on:context-menu={handleSearchResultContextMenu(result)}
                                        />
                                    </div>
                                {/each}
                            {:else}
                                <div class="search-results-message">
                                    No albums results...
                                </div>
                            {/if}
                        {:else if searchTab == "playlists"}
                            {#if results[searchOrigin].playlists}
                                {#each results[searchOrigin].playlists.data as result}
                                    <div class="search-results-song-item">
                                        <CollectionItem
                                            class="search-results-song-item"
                                            item={result}
                                            on:click={() => { dispatch("open-modal", result); }}
                                            on:context-menu={handleSearchResultContextMenu(result)}
                                        />
                                    </div>
                                {/each}
                            {:else}
                                <div class="search-results-message">
                                    No playlists results...
                                </div>
                            {/if}
                        {/if}
                    {:catch error}
                        <div class="search-results-message">
                            Press Enter to start searching...
                        </div>
                    {/await}
                </div>
            {/if}
        </div>
        <div class="option-group">
            <div class="option-header">Browse</div>
            {#each browseRoutes as { name, tag }}
                <div on:click={() => currentLocation.set(tag)}>
                    <Link to={tag}>
                        <div class="option" class:active={currentRoute == tag}>
                            {name}
                        </div>
                    </Link>
                </div>
            {/each}
        </div>
        <div
            class="option-group"
            on:dragenter={handleDragEnter}
            on:dragleave={handleDragLeave}
            on:dragover={handleDragOver}
            on:drop={handleDrop}
        >
            <div class="option-header">Library</div>
            {#each libraryRoutes as { name, tag }}
                <div on:click={() => currentLocation.set(tag)}>
                    <Link to={tag}>
                        <div class="option" class:active={currentRoute == tag}>
                            {name}
                        </div>
                    </Link>
                </div>
            {/each}
            {#if itemDragged}
                <div class="drop-hint {itemDraggedOver ? 'drop-zone' : ''}" />
            {/if}
        </div>
        <div
            class="option-group"
            on:dragenter={handleDragEnter}
            on:dragleave={handleDragLeave}
            on:dragover={handleDragOver}
            on:drop={handleDrop}
        >
            <div class="option-header">I/O</div>
            {#each miscRoutes as { name, tag }}
                <div on:click={() => currentLocation.set(tag)}>
                    <Link to={tag}>
                        <div class="option" class:active={currentRoute == tag}>
                            {name}
                        </div>
                    </Link>
                </div>
            {/each}
            {#if itemDragged}
                <div class="drop-hint {itemDraggedOver ? 'drop-zone' : ''}" />
            {/if}
        </div>
    </div>
    <div class="auth">
        <div class="auth-button" on:click={handleAuthButton}>
            {music.isAuthorized ? "Log out" : "Log in"}
        </div>
    </div>
    <div class="player">
        <Player on:context-menu />
    </div>
</div>

<svelte:window on:keydown={handleGlobalKeyDown} />

<style>
    .root {
        display: grid;
        grid-template-rows: min-content 1fr min-content 225px;
        grid-template-columns: 1fr;
        grid-template-areas: "title" "options" "auth" "player";
        width: 100%;
        min-height: 100%;
        text-align: center;
        color: var(--menu-fg-color, #171717);
        background-color: var(--menu-bg-color, #e0e0e0);
    }

    .title {
        grid-area: title;
        display: flex;
        align-items: center;
        justify-content: center;
        text-shadow: var(--menu-title-text-shadow);
        font-size: var(--menu-title-font-size);
        font-weight: var(--menu-title-font-weight);
        width: 100%;
        height: var(--menu-title-height);
    }

    .burger {
        position: absolute;
        right: 0;
        top: 0;
        width: 60px;
        height: var(--app-title-height);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .options {
        grid-area: options;
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .option-group {
        display: flex;
        flex-direction: column;
        position: relative;
    }

    .option-header {
        padding: 10px;
        font-size: 20px;
        font-weight: bold;
        text-align: center;
        background-color: var(--menu-opt-header-bg-color, #aaa);
        color: var(--menu-opt-header-fg-color, #171717);
    }

    .option {
        padding: 10px;
        box-sizing: border-box;
        font-size: 16px;
        font-weight: bold;
        background-color: var(--menu-opt-bg-color, #cfcfcf);
        color: var(--menu-opt-fg-color, #171717);
        text-align: left;
        cursor: pointer;
    }

    .option:hover,
    .option:focus {
        background-color: var(--menu-opt-header-bg-color, #aaa);
        color: var(--menu-opt-header-fg-color, #171717);
    }

    .option.active {
        color: var(--menu-opt-active-fg-color, #fff);
        background-color: var(--menu-opt-active-bg-color, #115aaa);
        text-shadow: 0 0 5px #000, 0 0 10px #000;
    }

    .search {
        position: relative;
        padding: 0px;
    }

    .search > input[type="text"] {
        width: 100%;
        height: 100%;
        font-size: 18px;
        font-weight: bold;
        font-family: inherit;
        text-align: center;
        border: none;
        background: transparent;
        box-sizing: border-box;
        padding: 10px;
        color: var(--menu-search-fg-color, #171717);
        background-color: var(--menu-search-bg-color, #cfcfcf);
        appearance: none;
        -ms-appearance: none;
        -moz-appearance: none;
        -webkit-appearance: none;
    }

    .search > input[type="text"]:focus {
        outline: none;
    }

    .search-results {
        display: flex;
        flex-direction: column;
        width: 100%;
        position: absolute;
        top: 100%;
        right: 0%;
        overflow: auto;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        z-index: 1;
    }

    .search-results-message {
        box-sizing: border-box;
        font-size: 16px;
        font-weight: bold;
        color: var(--menu-search-results-fg-color, #e0e0e0);
        background-color: var(--menu-search-results-bg-color, #272727);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 70px 10px;

        --loader-fg-color: var(--menu-search-results-fg-color, #e0e0e0);
        --loader-bg-color: var(--menu-search-results-bg-color, #272727);
        --loader-text-shadow: none;
    }

    .search-results-group-header {
        padding: 10px;
        font-size: 18px;
        font-weight: bold;
        text-align: center;
        color: var(--menu-search-results-header-fg-color, #aaa);
        background-color: var(--menu-search-results-header-bg-color, #171717);
    }

    .search-results-tabs-container {
        padding: 0;
    }

    .search-results-tabs-1 {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: repeat(2, 1fr);
    }

    .search-results-tabs-2 {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: repeat(3, 1fr);
    }

    .search-results-tab {
        padding: 10px 0;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .search-results-tab.active {
        color: var(--menu-search-results-header-bg-color, #171717);
        background-color: var(--menu-search-results-header-fg-color, #aaa);
    }

    .search-results-song-item {
        --song-item-padding: 10px;
        /* --song-item-bg-color: var(--menu-search-results-bg-color, #CFCFCF);
        --song-item-fg-color: var(--menu-search-results-fg-color, #272727);
        --song-item-hover-bg-color: var(--menu-search-results-fg-color, #272727);
        --song-item-hover-fg-color: var(--menu-search-results-bg-color, #CFCFCF);
        --song-item-secondary-fg-color: #BBBBBB;
        --song-item-secondary-bg-color: #707070;
        --song-item-hover-secondary-fg-color: #707070;
        --song-item-hover-secondary-bg-color: #BBBBBB; */

        --song-item-fg-color: var(--app-dark-fg-color);
        --song-item-bg-color: var(--app-dark-bg-color);
        --song-item-hover-fg-color: var(--app-light-fg-color);
        --song-item-hover-bg-color: var(--app-light-bg-color);
        --song-item-secondary-fg-color: var(--app-dark-secondary-fg-color);
        --song-item-hover-secondary-fg-color: var(--app-light-secondary-fg-color);

        --song-item-border-radius: 0;
    }

    .auth {
        grid-area: auth;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .auth-button {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-grow: 1;
        font-weight: bold;
        padding: 10px 20px;
        margin: 20px;
        text-align: center;
        border: var(--menu-auth-border, 2px #171717 solid);
        border-radius: 5px;
        cursor: pointer;
        transition: color background-color 0.2s;
        color: var(--menu-auth-fg-color, #171717);
        background-color: var(--menu-auth-bg-color, #e0e0e0);
    }

    .auth-button:hover {
        color: var(--menu-auth-hover-fg-color, #e0e0e0);
        background-color: var(--menu-auth-hover-bg-color, #171717);
    }

    .player {
        grid-area: player;
        width: 100%;
        height: 100%;
    }

    .drop-hint {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        box-sizing: border-box;
        border: dashed 10px #227fff;
    }

    .drop-zone {
        background-color: #227fff99;
        border: solid 10px #227fff;
    }

    @media (prefers-color-scheme: dark) {
        .search-results-song-item {
            --song-item-fg-color: #272727;
            --song-item-bg-color: #e0e0e0;
            --song-item-hover-fg-color: #e0e0e0;
            --song-item-hover-bg-color: #171717;
            --song-item-secondary-fg-color: #707070;
            --song-item-hover-secondary-fg-color: #cfcfcf;
        }
    }
</style>
